import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdPersonAdd, MdSearch, MdVisibility, MdWarning } from 'react-icons/md';
import api, { usuariosAPI } from '../services/api';
import { SkeletonTableRows, EmptyState } from '../components/SkeletonLoader';

const LeadsContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.xl};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    font-size: ${p => p.theme.fontSizes.xxl};
  }
`;

const Button = styled.button`
  padding: 10px ${p => p.theme.spacing.lg};
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  box-shadow: 0 4px 14px ${p => p.theme.colors.primary}40;

  svg { font-size: 1.2rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${p => p.theme.colors.primary}50;
  }
`;

const FilterSection = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 500px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${p => p.theme.spacing.md};
  padding-left: 2.5rem;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  transition: ${p => p.theme.transitions.fast};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    background-color: ${p => p.theme.colors.backgroundLight};
    outline: none;
  }

  &::placeholder {
    color: ${p => p.theme.colors.textLight};
  }
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: ${p => p.theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${p => p.theme.colors.textLight};
  font-size: 1.25rem;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  margin-top: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 7px 18px;
  background: ${p => p.active
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundDark};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  border: none;
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.active ? 600 : 500};
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  box-shadow: ${p => p.active ? `0 4px 12px ${p.theme.colors.primary}35` : 'none'};

  &:hover {
    background: ${p => p.active
      ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
      : p.theme.colors.border};
    color: ${p => p.active ? 'white' : p.theme.colors.textPrimary};
  }
`;

const ListSection = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const SectionTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 680px;
`;

const Th = styled.th`
  padding: 8px ${p => p.theme.spacing.md};
  text-align: left;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const Td = styled.td`
  padding: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
`;

const AvatarCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: ${p => p.bg || `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  letter-spacing: 0.02em;
`;

const NameLink = styled.button`
  color: ${p => p.theme.colors.textPrimary};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.medium};
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  transition: ${p => p.theme.transitions.fast};

  &:hover {
    color: ${p => p.theme.colors.primary};
    text-decoration: underline;
  }
`;

const StatusBadge = styled.span`
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.sm};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: ${p => p.theme.fontWeights.medium};
  background-color: ${p => {
    const s = p.status?.toLowerCase();
    if (s === 'nuevo') return p.theme.colors.primary + '20';
    if (s === 'contactado') return p.theme.colors.warning + '20';
    if (s === 'calificado') return p.theme.colors.success + '20';
    if (s === 'descartado') return p.theme.colors.textSecondary + '20';
    return p.theme.colors.primary + '20';
  }};
  color: ${p => {
    const s = p.status?.toLowerCase();
    if (s === 'nuevo') return p.theme.colors.primary;
    if (s === 'contactado') return p.theme.colors.warning;
    if (s === 'calificado') return p.theme.colors.success;
    if (s === 'descartado') return p.theme.colors.textSecondary;
    return p.theme.colors.primary;
  }};
`;

const ActionButton = styled.button`
  padding: 5px 14px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px ${p => p.theme.colors.primary}40;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  margin-top: ${p => p.theme.spacing.xl};
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  padding: 6px 14px;
  background: ${p => p.active
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundDark};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  border: none;
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.active ? 600 : 400};
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${p => p.active
      ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
      : p.theme.colors.border};
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const FILTERS = ['Todos', 'Nuevo', 'Contactado', 'Calificado', 'Descartado'];
const PER_PAGE = 10;

const getAvatarGradient = (nombre) => {
  const gradients = [
    'linear-gradient(135deg,#4FD1C5,#38B2AC)',
    'linear-gradient(135deg,#F6AD55,#ED8936)',
    'linear-gradient(135deg,#63B3ED,#4299E1)',
    'linear-gradient(135deg,#68D391,#48BB78)',
    'linear-gradient(135deg,#FC8181,#F56565)',
    'linear-gradient(135deg,#B794F4,#9F7AEA)',
  ];
  return gradients[(nombre?.charCodeAt(0) || 0) % gradients.length];
};

const getInitials = (nombre) =>
  (nombre || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

const StaleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 7px;
  border-radius: 99px;
  font-size: 0.65rem;
  font-weight: 700;
  background: ${p => p.theme.colors.warning}18;
  color: ${p => p.theme.colors.warning};
  svg { font-size: 0.7rem; }
`;

const AssignadoSelect = styled.select`
  padding: 7px 14px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textSecondary};
  cursor: pointer;
  margin-top: ${p => p.theme.spacing.md};
  transition: ${p => p.theme.transitions.fast};

  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const Leads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usuarios, setUsuarios] = useState([]);
  const [asignadoFilter, setAsignadoFilter] = useState('');

  useEffect(() => { loadLeads(); }, []);
  useEffect(() => {
    usuariosAPI.getAll().then(r => setUsuarios(r.data.data || [])).catch(() => {});
  }, []);
  useEffect(() => { setCurrentPage(1); }, [filter, searchTerm, asignadoFilter]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const res = await api.get('/leads');
      setLeads(res.data.data || res.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Error al cargar leads');
    } finally {
      setLoading(false);
    }
  };

  const filtered = leads.filter(l => {
    const matchFilter = filter === 'Todos' || l.estado?.toLowerCase() === filter.toLowerCase();
    const q = searchTerm.toLowerCase();
    const matchSearch = !q ||
      l.nombre?.toLowerCase().includes(q) ||
      l.empresa?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q);
    const matchAsignado = !asignadoFilter || String(l.asignado_a) === asignadoFilter;
    return matchFilter && matchSearch && matchAsignado;
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  const countByStatus = (s) =>
    leads.filter(l => l.estado?.toLowerCase() === s.toLowerCase()).length;

  return (
    <LeadsContainer>
      <PageHeader>
        <Title>Leads y Prospectos</Title>
        <Button onClick={() => navigate('/leads/nuevo')}>
          <MdPersonAdd />
          Nuevo Lead
        </Button>
      </PageHeader>

      <FilterSection>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Buscar por nombre, empresa o email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        <FilterButtons>
          <FilterButton active={filter === 'Todos'} onClick={() => setFilter('Todos')}>
            Todos ({leads.length})
          </FilterButton>
          {FILTERS.slice(1).map(f => (
            <FilterButton key={f} active={filter === f} onClick={() => setFilter(f)}>
              {f} ({countByStatus(f)})
            </FilterButton>
          ))}
        </FilterButtons>
        {usuarios.length > 0 && (
          <AssignadoSelect value={asignadoFilter} onChange={e => setAsignadoFilter(e.target.value)}>
            <option value="">Asignado a: Todos</option>
            {usuarios.map(u => (
              <option key={u.id} value={String(u.id)}>{u.nombre}</option>
            ))}
          </AssignadoSelect>
        )}
      </FilterSection>

      <ListSection>
        <SectionTitle>Lista de Leads ({filtered.length})</SectionTitle>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Empresa</Th>
                <Th>Telefono</Th>
                <Th>Origen</Th>
                <Th>Estado</Th>
                <Th>Fecha</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonTableRows rows={8} cols={7} />
              ) : paginated.length > 0 ? (
                paginated.map(lead => (
                  <tr key={lead.id}>
                    <Td>
                      <AvatarCell>
                        <Avatar bg={getAvatarGradient(lead.nombre)}>
                          {getInitials(lead.nombre)}
                        </Avatar>
                        <div>
                          <NameLink onClick={() => navigate(`/leads/${lead.id}`)}>
                            {lead.nombre}
                          </NameLink>
                          {lead.dias_sin_actividad >= 7 && !['ganado','perdido','descartado'].includes(lead.estado?.toLowerCase()) && (
                            <div>
                              <StaleBadge title={`${lead.dias_sin_actividad} días sin actividad`}>
                                <MdWarning />{lead.dias_sin_actividad}d sin actividad
                              </StaleBadge>
                            </div>
                          )}
                        </div>
                      </AvatarCell>
                    </Td>
                    <Td>{lead.empresa || 'N/A'}</Td>
                    <Td>{lead.telefono || 'N/A'}</Td>
                    <Td>{lead.origen || 'N/A'}</Td>
                    <Td>
                      <StatusBadge status={lead.estado}>
                        {lead.estado
                          ? lead.estado.charAt(0).toUpperCase() + lead.estado.slice(1)
                          : 'N/A'}
                      </StatusBadge>
                    </Td>
                    <Td>
                      {lead.created_at
                        ? new Date(lead.created_at).toLocaleDateString('es-MX')
                        : 'N/A'}
                    </Td>
                    <Td>
                      <ActionButton onClick={() => navigate(`/leads/${lead.id}`)}>
                        <MdVisibility />
                        Ver
                      </ActionButton>
                    </Td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <EmptyState
                      type="clients"
                      title={searchTerm || filter !== 'Todos' ? 'Sin resultados' : 'Sin leads aun'}
                      subtitle={
                        searchTerm || filter !== 'Todos'
                          ? 'Intenta con otro termino de busqueda o cambia el filtro.'
                          : 'Agrega tu primer lead para empezar a gestionar prospectos.'
                      }
                      actionLabel={!searchTerm && filter === 'Todos' ? 'Agregar Lead' : undefined}
                      onAction={!searchTerm && filter === 'Todos' ? () => navigate('/leads/nuevo') : undefined}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrapper>

        {totalPages > 1 && (
          <Pagination>
            <PageButton onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
              Anterior
            </PageButton>
            {getPageNumbers().map((page, i) =>
              page === '...' ? (
                <span key={`e-${i}`} style={{ padding: '0 8px' }}>...</span>
              ) : (
                <PageButton
                  key={page}
                  active={currentPage === page}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </PageButton>
              )
            )}
            <PageButton onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
              Siguiente
            </PageButton>
          </Pagination>
        )}
      </ListSection>
    </LeadsContainer>
  );
};

export default Leads;
